import {
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';

import { makeStyles } from '@mui/styles';
import { useMediaQuery, useScrollTrigger } from '@mui/material';
import { useTheme, alpha } from '@classify/react-core-components/styles';
import browser from '@openclassrooms/ui-web/browser';
import { scrollToElement } from '@openclassrooms/ui-web/scroll';
import { TabBar } from '@openclassrooms/ui-web/navigation';
import TAB_BAR_VARIANTS from '@openclassrooms/ui-web/navigation/constants/tabBarVariants';
import { translate } from '@openclassrooms/translations';
import ContentWrapper from '@classify/react-core-components/ContentWrapper';
import Typography from '@classify/react-core-components/Typography';
import useIsElementVisible from '../../hooks/useIsElementVisible';
import StartNowButton from '../StartNowButton';
import CourseContext from '../../contexts/courseContext';
import ContinueCourseButton from '../ContinueCourseButton';
import FollowCourseButton from '../FollowCourseButton';
import COURSE_TABS from '../../constants/courseTabs';
import useCourseStatus from '../../hooks/useCourseStatus';

const COURSE_TABBAR_HEIGHT = 60;

interface Props {
  footerRef: RefObject<HTMLDivElement>;
}

const useStyles = makeStyles(
  ({ palette, typography, spacing, shadows, breakpoints, zIndex }) => ({
    root: {
      position: 'sticky',
      top: '0',
      display: 'block',
      width: '100%',
      minHeight: COURSE_TABBAR_HEIGHT,
      backgroundColor: palette.background.primary,
      borderBottom: `1px solid var(--borderColor)`,
      zIndex: zIndex.appBar - 1,
    },
    wrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      height: COURSE_TABBAR_HEIGHT,
      [breakpoints.up('sm')]: {
        height: 'unset',
      },
    },
    paper: {
      background: 'transparent',
      boxShadow: 'none',
    },
    isSticky: {
      backgroundColor: palette.background.default,
      borderBottom: 0,
      boxShadow: shadows[4],
      maxHeight: COURSE_TABBAR_HEIGHT,
      [breakpoints.up('sm')]: {
        maxHeight: 'unset',
        paddingTop: spacing(2),
      },
    },
    tab: {
      color: palette.text.primary,
      fontWeight: typography.fontWeightRegular,
      marginTop: spacing(1.5),
      '&:hover': {
        color: 'var(--categoryColor)',
      },
      '&$tabSelected': {
        color: 'var(--categoryColor)',
        fontWeight: typography.fontWeightBold,
      },
    },
    tabSticky: {
      marginTop: 0,
    },
    tabSelected: {},
    tabIndicator: {
      backgroundColor: 'var(--categoryColor)',
    },
    buttonContainer: {
      alignSelf: 'center',
    },
    title: {
      [breakpoints.up('sm')]: {
        maxWidth: 481,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      [breakpoints.up('md')]: {
        maxWidth: 710,
      },
      [breakpoints.up('lg')]: {
        maxWidth: 940,
      },
    },
  }),
  { classNamePrefix: 'CourseTabBar' }
);

const CourseTabBar = ({ footerRef }: Props) => {
  const classes = useStyles();
  const { breakpoints } = useTheme();
  const isDesktop = useMediaQuery(breakpoints.up('sm'));
  const tabsRef = useRef<HTMLDivElement>(null);
  const [currentlyVisibleSection, setCurrentlyVisibleSection] = useState(0);
  const [offset, setOffset] = useState(0);
  const { course } = useContext(CourseContext);
  const { pathname } = useLocation();
  const { isFinished, hasAlreadyFollowed, notStarted, isFollowing } = useCourseStatus();
  const { title: courseTitle, categoryColor } = course;
  const TAB_SCROLL_OFFSET = 16;

  const isFooterVisible = useIsElementVisible(footerRef, 0.5);

  useLayoutEffect(() => {
    if (!tabsRef.current) {
      return;
    }

    setOffset(tabsRef.current.offsetTop);
  }, [tabsRef]);

  const hasScrolledToTop = useScrollTrigger({
    disableHysteresis: true,
    threshold: offset,
  });

  const shouldDisplayCourseTitle = hasScrolledToTop && isDesktop;
  const shouldDisplayCta = hasScrolledToTop && !isFooterVisible && isDesktop && !isFinished;

  const items = useMemo(() => {
    const { TAB_OVERVIEW, TAB_TOC, TAB_CREDITS } = COURSE_TABS;

    return [
      {
        title: translate('course.overview.tab'),
        route: `${pathname}#${TAB_OVERVIEW}`,
        id: TAB_OVERVIEW,
      },
      {
        title: translate('course.toc.tab'),
        route: `${pathname}#${TAB_TOC}`,
        id: TAB_TOC,
      },
      {
        title: translate('course.teachers.tab'),
        route: `${pathname}#${TAB_CREDITS}`,
        id: TAB_CREDITS,
      },
    ];
  }, [pathname]);

  useEffect(() => {
    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const currentlyVisibleSectionIndex = items.findIndex(
            (item) => item.id === entry.target.getAttribute('id')
          );
          setCurrentlyVisibleSection(currentlyVisibleSectionIndex);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: '-30% 0px -70% 0px',
      threshold: 0,
    });

    items.forEach((item) => {
      const domElement = document.getElementById(item.id);

      if (domElement) {
        observer.observe(domElement);
      }
    });
  }, [items]);

  const tabClasses = {
    root: cx(classes.tab, { [classes.tabSticky]: hasScrolledToTop }),
    selected: classes.tabSelected,
  };

  return (
    <div
      className={cx(classes.root, {
        [classes.isSticky]: hasScrolledToTop,
      })}
      ref={tabsRef}
      style={{
        '--categoryColor': categoryColor,
        '--borderColor': alpha(categoryColor, 0.24),
      }}
    >
      <ContentWrapper className={classes.wrapper}>
        <div>
          {shouldDisplayCourseTitle && (
            <Typography variant="h6" component="p" className={classes.title}>
              {courseTitle}
            </Typography>
          )}
          <TabBar
            isScrollable
            variant={TAB_BAR_VARIANTS.STICKY}
            selectedTabIndex={currentlyVisibleSection}
            onChange={(index: number) => {
              scrollToElement(browser.document.getElementById(items[index].id), {
                offset: tabsRef?.current?.clientHeight + TAB_SCROLL_OFFSET || 0,
              });
            }}
            tabsClasses={{ indicator: classes.tabIndicator }}
            paperClasses={{ root: classes.paper }}
          >
            {items.map(({ title, id, route }) => (
              <TabBar.Item
                classes={tabClasses}
                role="tab"
                key={id}
                component="a"
                to={route}
                title={title}
              />
            ))}
          </TabBar>
        </div>
        {shouldDisplayCta && (
          <div className={classes.buttonContainer}>
            {notStarted && <StartNowButton />}
            {isFollowing && <ContinueCourseButton />}
            {hasAlreadyFollowed && (
              <FollowCourseButton>{translate('course.reenroll.button')}</FollowCourseButton>
            )}
          </div>
        )}
      </ContentWrapper>
    </div>
  );
};

export default CourseTabBar;
